// Native melody editing on Voice items (slice 4, PR 4) — the score-model
// port of melodyEdit.ts, closing the migration's editing seam.
//
// Ops address EVENTS (which the Score page renders and selects), so content
// that only exists in the model — explicit rests, the tail pieces of tied
// splits — is finally editable: the chart projection drops rests, so the old
// chart-path delete was a silent no-op on them.
//
// Semantics are melodyEdit's, replace-entry included (the July 2026 ruling):
// a note entered over existing content consumes it — the overlapped note
// keeps a shortened lead (dangling tie-starts dropped), the trailing
// remainder becomes a rest, fully covered events vanish, and entry clamps to
// the barline. Internally every op flattens the voice to exact-Fraction
// timed slots, edits the slots, and rebuilds written items through the same
// slotsToItems the chart lift uses — so triplet grouping and tied splitting
// cannot diverge between editing and importing.
//
// Ops take a Score and return a new Score (or null for a recognised no-op);
// they never mutate their input. The editor works in CHART BEATS (the meter
// denominator unit), matching the UI and command layer.

import { uid } from '../song';
import { keyLabelOf, slotsToItems, UnnotatableDurationError, type WrittenSlot } from './convert';
import {
  addF,
  compareF,
  frac,
  fractionFromNumber,
  fractionToNumber,
  measureQuarters,
  mulF,
  type Fraction,
} from './duration';
import { midiOf, noteNameOf, parseSpelledPitchName, spellMidi } from './pitch';
import { parseKey, CHROMATIC_FLAT, CHROMATIC_SHARP, pitchClassOf } from '../notes';
import { voiceLength, voiceTiming } from './timing';
import type {
  Articulation,
  DynamicMark,
  NoteEvent,
  NoteEventMetadata,
  NoteHead,
  Part,
  Score,
  Voice,
  VoiceItem,
} from './types';

// ---------------------------------------------------------------------------
// Timed slots (the editing domain)
// ---------------------------------------------------------------------------

interface TimedSlot {
  start: Fraction;
  quarters: Fraction;
  idBase: string;
  tupletGroup?: string;
  noteheads?: NoteHead[];
  /** Authored event metadata to retain when a structural edit rebuilds slots. */
  source?: NoteEvent;
  metadata?: NoteEventMetadata;
}

const ZERO = frac(0);
const negate = (f: Fraction) => mulF(f, frac(-1));

function voiceToSlots(voice: Voice | undefined): TimedSlot[] {
  if (!voice) return [];
  const groupByEvent = new Map<string, string>();
  for (const item of voice.items) {
    if (item.kind !== 'tuplet') continue;
    for (const event of flattenEvents(item.items)) groupByEvent.set(event.id, item.id);
  }
  return voiceTiming(voice)
    .filter((t) => !t.event.grace && compareF(t.duration, ZERO) > 0)
    .map((t) => ({
      start: t.start,
      quarters: t.duration,
      idBase: t.event.id,
      ...(groupByEvent.has(t.event.id) ? { tupletGroup: groupByEvent.get(t.event.id)! } : {}),
      source: t.event,
      ...(t.event.kind === 'note' ? { noteheads: t.event.notes.map((nh) => ({ ...nh })) } : {}),
    }));
}

/** The slot writer can reconstruct straight values and top-level 3:2
 * triplets. Other ratios (and nested tuplets) must remain as structural
 * subtrees: flattening their exact sounding fractions cannot recreate them. */
function requiresExactTupletPreservation(voice: Voice | undefined): boolean {
  const visit = (items: VoiceItem[], depth: number): boolean =>
    items.some((item) => {
      if (item.kind !== 'tuplet') return false;
      if (depth > 0 || item.actual !== 3 || item.normal !== 2) return true;
      return visit(item.items, depth + 1);
    });
  return voice ? visit(voice.items, 0) : false;
}

/**
 * Replace-entry carve: remove [start, end) from the slots. An overlapped
 * note keeps its leading remainder (dangling tie-start dropped — its
 * continuation is gone), the trailing remainder becomes a rest, and rests
 * trim to rests.
 */
function carveSlots(slots: TimedSlot[], start: Fraction, end: Fraction): TimedSlot[] {
  const out: TimedSlot[] = [];
  for (const s of slots) {
    const sEnd = addF(s.start, s.quarters);
    if (compareF(sEnd, start) <= 0 || compareF(s.start, end) >= 0) {
      out.push(s);
      continue;
    }
    if (compareF(s.start, start) < 0) {
      const lead: TimedSlot = { ...s, quarters: addF(start, negate(s.start)) };
      if (lead.noteheads) {
        lead.noteheads = lead.noteheads.map((nh) => {
          const keepStop = nh.tie === 'stop' || nh.tie === 'both';
          const next: NoteHead = { pitch: nh.pitch };
          if (keepStop) next.tie = 'stop';
          return next;
        });
      }
      out.push(lead);
    }
    if (compareF(sEnd, end) > 0) {
      out.push({ start: end, quarters: addF(sEnd, negate(end)), idBase: uid('r') });
    }
  }
  return out;
}

function slotOverlaps(slot: TimedSlot, start: Fraction, end: Fraction): boolean {
  return compareF(addF(slot.start, slot.quarters), start) > 0 && compareF(slot.start, end) < 0;
}

/** Sort, fill gaps + tail with rests, and rebuild the written items. */
function slotsToVoiceItems(
  slots: TimedSlot[],
  capacity: Fraction,
  restScope: string,
): Voice['items'] {
  const sorted = [...slots].sort((a, b) => compareF(a.start, b.start));
  const contiguous: WrittenSlot[] = [];
  let cursor = ZERO;
  for (const s of sorted) {
    if (compareF(s.start, cursor) > 0) {
      contiguous.push({
        quarters: addF(s.start, negate(cursor)),
        idBase: restId(restScope, cursor),
      });
    }
    contiguous.push({
      quarters: s.quarters,
      idBase: s.idBase,
      ...(s.tupletGroup ? { tupletGroup: s.tupletGroup } : {}),
      ...(s.source ? { source: s.source } : {}),
      ...(s.metadata ? { metadata: s.metadata } : {}),
      ...(s.noteheads ? { noteheads: s.noteheads } : {}),
    });
    cursor = addF(s.start, s.quarters);
  }
  const tail = addF(capacity, negate(cursor));
  if (compareF(tail, ZERO) > 0) {
    contiguous.push({ quarters: tail, idBase: restId(restScope, cursor) });
  }
  return slotsToItems(contiguous);
}

/** Deterministic rest id from its exact score position and owning voice. */
function restId(scope: string, at: Fraction): string {
  return `r:${scope}:q${at.num}_${at.den}`;
}

function restScopeFor(
  score: Score,
  measureIndex: number,
  voiceIndex: number,
  target?: ContentTarget,
): string {
  const resolved = resolveTarget(score, target);
  const partId = resolved?.partId ?? score.parts[0]?.id ?? 'part:lead';
  const staffIndex = resolved?.staffIndex ?? 0;
  return `${score.form[measureIndex].id}:${partId}:s${staffIndex}:v${voiceIndex}`;
}

// ---------------------------------------------------------------------------
// Grace preservation (graces occupy no time, so they live OUTSIDE the slot
// machinery: extracted before a rebuild, re-anchored to their principal after)
// ---------------------------------------------------------------------------

interface GraceRuns {
  /** Grace runs keyed by the id of the non-grace event they precede. */
  byAnchor: Map<string, NoteEvent[]>;
  /** A dangling run at the end of the voice (no principal follows). */
  trailing: NoteEvent[];
}

function flattenEvents(items: VoiceItem[], out: NoteEvent[] = []): NoteEvent[] {
  for (const item of items) {
    if (item.kind === 'tuplet') flattenEvents(item.items, out);
    else out.push(item);
  }
  return out;
}

function extractGraceRuns(items: VoiceItem[]): GraceRuns {
  const byAnchor = new Map<string, NoteEvent[]>();
  let run: NoteEvent[] = [];
  for (const event of flattenEvents(items)) {
    if (event.grace) {
      run.push(event);
      continue;
    }
    if (run.length > 0) byAnchor.set(event.id, run);
    run = [];
  }
  return { byAnchor, trailing: run };
}

/** Splice each run back in front of its anchor (recursing into tuplets).
 *  Runs whose anchor no longer exists were attached to deleted/overwritten
 *  content and vanish with it — the same fate as the principal. */
function insertGraceRuns(items: VoiceItem[], runs: GraceRuns): VoiceItem[] {
  const splice = (list: VoiceItem[]): VoiceItem[] =>
    list.flatMap((item): VoiceItem[] => {
      if (item.kind === 'tuplet') return [{ ...item, items: splice(item.items) as NoteEvent[] }];
      const run = runs.byAnchor.get(item.id);
      if (!run) return [item];
      runs.byAnchor.delete(item.id);
      return [...run, item];
    });
  const out = splice(items);
  return runs.trailing.length > 0 ? [...out, ...runs.trailing] : out;
}

// ---------------------------------------------------------------------------
// Score plumbing
// ---------------------------------------------------------------------------

function timeSigAt(score: Score, index: number): [number, number] {
  let sig: [number, number] = [4, 4];
  for (let i = 0; i <= index && i < score.form.length; i++) {
    const s = score.form[i].timeSig;
    if (s) sig = s;
  }
  return sig;
}

/** Chart beats (meter denominator units) → quarters, at a measure. */
function beatQuarters(score: Score, index: number): Fraction {
  return frac(4, timeSigAt(score, index)[1]);
}

/** Written capacity of one form measure. Pickups use their explicit beat
 * length rather than the full inherited meter. */
function measureCapacity(score: Score, index: number): Fraction {
  const pickup = score.form[index]?.pickupBeats;
  return pickup == null
    ? measureQuarters(timeSigAt(score, index))
    : mulF(fractionFromNumber(pickup), beatQuarters(score, index));
}

function isGeneratedPaddingEvent(event: NoteEvent): boolean {
  return event.kind === 'rest' && /^r[:-]/.test(event.id);
}

function authoredEventIds(items: VoiceItem[]): string[] {
  return flattenEvents(items)
    .filter((event) => !isGeneratedPaddingEvent(event))
    .map((event) => event.id);
}

/**
 * Rebuild editor-generated gap/tail rests against the capacities now in force.
 * Authored slots keep their exact onsets and metadata; only deterministic
 * `r:` / `r-` padding is discarded and regenerated. Arbitrary or nested
 * tuplets are left byte-for-byte alone unless they overflow, in which case a
 * safe reflow is impossible and the caller must reject the form edit.
 */
export function reflowGeneratedPadding(
  score: Score,
  measureIndexes: readonly number[],
): Score | null {
  const affected = new Set(
    measureIndexes.filter((index) => index >= 0 && index < score.form.length),
  );
  if (affected.size === 0 || score.parts.length === 0) return score;

  let failed = false;
  let changed = false;
  const parts = score.parts.map((part) => {
    const measures = part.measures.map((measure, measureIndex) => {
      if (!affected.has(measureIndex)) return measure;
      const capacity = measureCapacity(score, measureIndex);
      const voiceIndexes = new Map<number, number>();
      const voices = measure.voices.map((voice) => {
        const staffIndex = staffOf(voice);
        const voiceIndex = voiceIndexes.get(staffIndex) ?? 0;
        voiceIndexes.set(staffIndex, voiceIndex + 1);
        const slots = voiceToSlots(voice);
        const hasGeneratedPadding = slots.some(
          (slot) => !slot.noteheads && /^r[:-]/.test(slot.idBase),
        );
        if (!hasGeneratedPadding) {
          if (compareF(voiceLength(voice), capacity) > 0) failed = true;
          return voice;
        }
        if (requiresExactTupletPreservation(voice)) {
          if (compareF(voiceLength(voice), capacity) > 0) failed = true;
          return voice;
        }

        const authoredSlots = slots.filter((slot) => slot.noteheads || !/^r[:-]/.test(slot.idBase));
        if (authoredSlots.some((slot) => compareF(addF(slot.start, slot.quarters), capacity) > 0)) {
          failed = true;
          return voice;
        }

        const rebuilt = insertGraceRuns(
          slotsToVoiceItems(
            authoredSlots,
            capacity,
            `${score.form[measureIndex].id}:${part.id}:s${staffIndex}:v${voiceIndex}`,
          ),
          extractGraceRuns(voice.items),
        );
        const beforeIds = authoredEventIds(voice.items);
        const afterIds = authoredEventIds(rebuilt);
        if (
          beforeIds.length !== afterIds.length ||
          beforeIds.some((id, index) => id !== afterIds[index])
        ) {
          failed = true;
          return voice;
        }
        changed = true;
        return { ...voice, items: rebuilt };
      });
      return voices === measure.voices ? measure : { ...measure, voices };
    });
    return { ...part, measures };
  });

  if (failed) return null;
  return changed ? withoutDanglingSpanners({ ...score, parts }) : score;
}

/** Identifies one authored staff. Voice indexes passed to editor operations
 * are local to this staff, rather than indexes into the part-wide Voice[]. */
export interface ContentTarget {
  partId: string;
  staffIndex: number;
}

interface ResolvedTarget extends ContentTarget {
  partIndex: number;
  part: Part;
}

/** Legacy callers edit part 0 / staff 0. Explicit targets use the stable part
 * id so adding or reordering instruments cannot redirect an edit. */
function resolveTarget(score: Score, target?: ContentTarget): ResolvedTarget | null {
  const partIndex = target
    ? score.parts.findIndex((part) => part.id === target.partId)
    : score.parts.length > 0
      ? 0
      : -1;
  if (partIndex < 0) return null;
  const part = score.parts[partIndex];
  const staffIndex = target?.staffIndex ?? 0;
  if (staffIndex < 0 || staffIndex >= part.staves.length) return null;
  return { partId: part.id, staffIndex, partIndex, part };
}

function staffOf(voice: Voice): number {
  return voice.staff ?? 0;
}

function staffVoiceEntries(
  part: Part,
  measureIndex: number,
  staffIndex: number,
): Array<{ voice: Voice; voiceIndex: number; partVoiceIndex: number }> {
  let voiceIndex = 0;
  const entries: Array<{ voice: Voice; voiceIndex: number; partVoiceIndex: number }> = [];
  const voices = part.measures[measureIndex]?.voices ?? [];
  for (let partVoiceIndex = 0; partVoiceIndex < voices.length; partVoiceIndex++) {
    const voice = voices[partVoiceIndex];
    if (staffOf(voice) !== staffIndex) continue;
    entries.push({ voice, voiceIndex, partVoiceIndex });
    voiceIndex++;
  }
  return entries;
}

function voiceAtTarget(
  score: Score,
  measureIndex: number,
  voiceIndex: number,
  target?: ContentTarget,
): Voice | undefined {
  const resolved = resolveTarget(score, target);
  return resolved
    ? staffVoiceEntries(resolved.part, measureIndex, resolved.staffIndex)[voiceIndex]?.voice
    : undefined;
}

/** Replace one measure's items at a voice index, creating the Lead part on
 *  demand (ids follow the chart-lift scheme so identities stay uniform).
 *  Lower voice slots are padded with empty placeholders when writing a higher
 *  voice; trailing empty voices are trimmed back to the normal form. */
function withVoiceItems(
  score: Score,
  index: number,
  items: Voice['items'],
  voiceIndex = 0,
  target?: ContentTarget,
): Score {
  const measureId = score.form[index]?.id ?? `i${index}`;
  let resolved = resolveTarget(score, target);
  let parts = score.parts;
  if (!resolved) {
    // Creating the historical Lead part is a legacy convenience. An explicit
    // target must already exist because a part also needs authored metadata
    // (name, clef, transposition) that this low-level operation cannot infer.
    if (target) return score;
    if (items.length === 0) return score;
    const part: Part = {
      id: 'part:lead',
      name: 'Lead',
      instrument: 'lead',
      role: 'lead',
      staves: [{ id: 'staff:lead:0', clef: 'treble8vb', staffType: 'standard' }],
      measures: score.form.map((m) => ({ id: `pm:${m.id}`, voices: [] })),
    };
    parts = [part];
    resolved = { partId: part.id, staffIndex: 0, partIndex: 0, part };
  }
  const { part, partIndex, staffIndex } = resolved;
  const measures = part.measures.map((pm, i) => {
    if (i !== index) return pm;
    const voices = [...pm.voices];
    let entries = voices
      .map((voice, partVoiceIndex) => ({ voice, partVoiceIndex }))
      .filter((entry) => staffOf(entry.voice) === staffIndex)
      .map((entry, localIndex) => ({ ...entry, voiceIndex: localIndex }));
    while (entries.length <= voiceIndex) {
      const created: Voice = {
        // Preserve the historical id shape for legacy part-0/staff-0 calls.
        id: target
          ? `v:${measureId}:s${staffIndex}:${entries.length}`
          : `v:${measureId}:${voices.length}`,
        ...(target || staffIndex !== 0 ? { staff: staffIndex } : {}),
        items: [],
      };
      // Keep staff groups in score order where possible. This is presentation-
      // neutral, but produces conventional RH-before-LH piano Voice arrays.
      const afterSameStaff = entries.at(-1)?.partVoiceIndex;
      const beforeLaterStaff = voices.findIndex((voice) => staffOf(voice) > staffIndex);
      const insertAt =
        afterSameStaff !== undefined
          ? afterSameStaff + 1
          : beforeLaterStaff >= 0
            ? beforeLaterStaff
            : voices.length;
      voices.splice(insertAt, 0, created);
      entries = voices
        .map((voice, partVoiceIndex) => ({ voice, partVoiceIndex }))
        .filter((entry) => staffOf(entry.voice) === staffIndex)
        .map((entry, localIndex) => ({ ...entry, voiceIndex: localIndex }));
    }
    const entry = entries[voiceIndex];
    // Slot rebuilds see only timed content; carry the voice's grace notes
    // across, re-anchored to their surviving principals.
    const rebuilt =
      items.length > 0 ? insertGraceRuns(items, extractGraceRuns(entry.voice.items)) : items;
    voices[entry.partVoiceIndex] = {
      ...entry.voice,
      ...(target || entry.voice.staff !== undefined ? { staff: staffIndex } : {}),
      items: rebuilt,
    };

    // Empty voices at the end of THIS staff's local sequence are redundant.
    // Leading empty placeholders remain when a higher local voice has content.
    const targetEntries = voices
      .map((voice, partVoiceIndex) => ({ voice, partVoiceIndex }))
      .filter((candidate) => staffOf(candidate.voice) === staffIndex);
    let lastAuthored = targetEntries.length - 1;
    while (lastAuthored >= 0 && targetEntries[lastAuthored].voice.items.length === 0)
      lastAuthored--;
    const redundant = new Set(
      targetEntries.slice(lastAuthored + 1).map((candidate) => candidate.partVoiceIndex),
    );
    const trimmed = voices.filter((_, partVoiceIndex) => !redundant.has(partVoiceIndex));
    return { ...pm, voices: trimmed };
  });
  return {
    ...score,
    parts: parts.map((candidate, candidateIndex) =>
      candidateIndex === partIndex ? { ...part, measures } : candidate,
    ),
  };
}

export interface FoundEvent {
  partId: string;
  partIndex: number;
  staffIndex: number;
  measureIndex: number;
  /** Voice index local to staffIndex. */
  voiceIndex: number;
  /** Index into PartMeasure.voices, for internal model rewrites. */
  partVoiceIndex: number;
  slotIndex: number;
  slots: TimedSlot[];
  slot: TimedSlot;
}

export interface EventAtPosition {
  id: string;
  partId: string;
  partIndex: number;
  staffIndex: number;
  measureIndex: number;
  voiceIndex: number;
  midi: number | null;
  beat: number;
  duration: number;
  rest: boolean;
}

/** Locate an event by id across all parts, or within an explicit staff. */
export function findEvent(score: Score, id: string, target?: ContentTarget): FoundEvent | null {
  const parts = target
    ? (() => {
        const resolved = resolveTarget(score, target);
        return resolved ? [resolved] : [];
      })()
    : score.parts.map((part, partIndex) => ({
        part,
        partIndex,
        partId: part.id,
        staffIndex: -1,
      }));
  for (const resolved of parts) {
    const { part, partIndex, partId } = resolved;
    for (let i = 0; i < part.measures.length; i++) {
      const localByStaff = new Map<number, number>();
      const voices = part.measures[i].voices;
      for (let v = 0; v < voices.length; v++) {
        const staffIndex = staffOf(voices[v]);
        const voiceIndex = localByStaff.get(staffIndex) ?? 0;
        localByStaff.set(staffIndex, voiceIndex + 1);
        if (resolved.staffIndex >= 0 && staffIndex !== resolved.staffIndex) continue;
        const slots = voiceToSlots(voices[v]);
        const slotIndex = slots.findIndex((s) => s.idBase === id);
        if (slotIndex >= 0) {
          return {
            partId,
            partIndex,
            staffIndex,
            measureIndex: i,
            voiceIndex,
            partVoiceIndex: v,
            slotIndex,
            slots,
            slot: slots[slotIndex],
          };
        }
      }
    }
  }
  return null;
}

/** Exact onset lookup for the entry caret. It stays in the score's Fraction
 * timing domain, so tuplets and a second voice do not depend on a lossy Song
 * projection. Synthetic padding rests are returned too; callers can identify
 * those by their deterministic `r:` id when deciding whether to select them. */
export function findEventAt(
  score: Score,
  measureId: string,
  voiceIndex: number,
  beat: number,
  target?: ContentTarget,
): EventAtPosition | null {
  const measureIndex = score.form.findIndex((measure) => measure.id === measureId);
  if (measureIndex < 0) return null;
  const bq = beatQuarters(score, measureIndex);
  const start = mulF(fractionFromNumber(beat - 1), bq);
  const resolved = resolveTarget(score, target);
  if (!resolved) return null;
  const voice = staffVoiceEntries(resolved.part, measureIndex, resolved.staffIndex)[voiceIndex]
    ?.voice;
  const slot = voiceToSlots(voice).find((candidate) => compareF(candidate.start, start) === 0);
  if (!slot) return null;
  const toBeats = (quarters: Fraction) => fractionToNumber(quarters) / fractionToNumber(bq);
  return {
    id: slot.idBase,
    partId: resolved.partId,
    partIndex: resolved.partIndex,
    staffIndex: resolved.staffIndex,
    measureIndex,
    voiceIndex,
    midi: editableMidi(slot.noteheads),
    beat: 1 + toBeats(slot.start),
    duration: toBeats(slot.quarters),
    rest: !slot.noteheads,
  };
}

/** Nearest event onset strictly before the caret in the same voice/measure. */
export function findEventBefore(
  score: Score,
  measureId: string,
  voiceIndex: number,
  beat: number,
  target?: ContentTarget,
): EventAtPosition | null {
  const measureIndex = score.form.findIndex((measure) => measure.id === measureId);
  if (measureIndex < 0) return null;
  const bq = beatQuarters(score, measureIndex);
  const targetStart = mulF(fractionFromNumber(beat - 1), bq);
  const resolved = resolveTarget(score, target);
  if (!resolved) return null;
  const voice = staffVoiceEntries(resolved.part, measureIndex, resolved.staffIndex)[voiceIndex]
    ?.voice;
  const slot = voiceToSlots(voice)
    .filter((candidate) => compareF(candidate.start, targetStart) < 0)
    .sort((a, b) => compareF(b.start, a.start))[0];
  if (!slot) return null;
  const toBeats = (quarters: Fraction) => fractionToNumber(quarters) / fractionToNumber(bq);
  return {
    id: slot.idBase,
    partId: resolved.partId,
    partIndex: resolved.partIndex,
    staffIndex: resolved.staffIndex,
    measureIndex,
    voiceIndex,
    midi: editableMidi(slot.noteheads),
    beat: 1 + toBeats(slot.start),
    duration: toBeats(slot.quarters),
    rest: !slot.noteheads,
  };
}

/** Last timed event onset in one voice/measure. Unlike a grid-derived bar-end
 * position, this retains the exact Fraction timing of arbitrary tuplets. */
export function findLastEvent(
  score: Score,
  measureId: string,
  voiceIndex: number,
  target?: ContentTarget,
): EventAtPosition | null {
  const measureIndex = score.form.findIndex((measure) => measure.id === measureId);
  if (measureIndex < 0) return null;
  const bq = beatQuarters(score, measureIndex);
  const resolved = resolveTarget(score, target);
  if (!resolved) return null;
  const voice = staffVoiceEntries(resolved.part, measureIndex, resolved.staffIndex)[voiceIndex]
    ?.voice;
  const slot = voiceToSlots(voice).sort((a, b) => compareF(b.start, a.start))[0];
  if (!slot) return null;
  const toBeats = (quarters: Fraction) => fractionToNumber(quarters) / fractionToNumber(bq);
  return {
    id: slot.idBase,
    partId: resolved.partId,
    partIndex: resolved.partIndex,
    staffIndex: resolved.staffIndex,
    measureIndex,
    voiceIndex,
    midi: editableMidi(slot.noteheads),
    beat: 1 + toBeats(slot.start),
    duration: toBeats(slot.quarters),
    rest: !slot.noteheads,
  };
}

function keyContextOf(score: Score, measureIndex = 0) {
  let keySig = score.form[0]?.keySig;
  for (let i = 0; i <= measureIndex && i < score.form.length; i++) {
    if (score.form[i].keySig) keySig = score.form[i].keySig;
  }
  return parseKey(keyLabelOf(keySig) ?? null);
}

// ---------------------------------------------------------------------------
// Grace-event editing (graces are real, selectable events but occupy no time,
// so they bypass the slot machinery: patched or removed in place)
// ---------------------------------------------------------------------------

interface FoundModelEvent {
  partId: string;
  partIndex: number;
  staffIndex: number;
  measureIndex: number;
  /** Voice index local to staffIndex. */
  voiceIndex: number;
  /** Index into PartMeasure.voices. */
  partVoiceIndex: number;
  event: NoteEvent;
  insideTuplet: boolean;
}

/** Locate the authored event itself rather than its flattened timed slot. */
function findModelEvent(score: Score, id: string, target?: ContentTarget): FoundModelEvent | null {
  const locate = (
    items: VoiceItem[],
    insideTuplet = false,
  ): Pick<FoundModelEvent, 'event' | 'insideTuplet'> | null => {
    for (const item of items) {
      if (item.kind === 'tuplet') {
        const nested = locate(item.items, true);
        if (nested) return nested;
      } else if (item.id === id) {
        return { event: item, insideTuplet };
      }
    }
    return null;
  };
  const parts = target
    ? (() => {
        const resolved = resolveTarget(score, target);
        return resolved ? [resolved] : [];
      })()
    : score.parts.map((part, partIndex) => ({
        part,
        partIndex,
        partId: part.id,
        staffIndex: -1,
      }));
  for (const resolved of parts) {
    const { part, partIndex, partId } = resolved;
    for (let i = 0; i < part.measures.length; i++) {
      const localByStaff = new Map<number, number>();
      const voices = part.measures[i].voices;
      for (let v = 0; v < voices.length; v++) {
        const staffIndex = staffOf(voices[v]);
        const voiceIndex = localByStaff.get(staffIndex) ?? 0;
        localByStaff.set(staffIndex, voiceIndex + 1);
        if (resolved.staffIndex >= 0 && staffIndex !== resolved.staffIndex) continue;
        const located = locate(voices[v].items);
        if (located) {
          return {
            partId,
            partIndex,
            staffIndex,
            measureIndex: i,
            voiceIndex,
            partVoiceIndex: v,
            ...located,
          };
        }
      }
    }
  }
  return null;
}

function findGraceEvent(score: Score, id: string, target?: ContentTarget): FoundModelEvent | null {
  const found = findModelEvent(score, id, target);
  return found?.event.grace ? found : null;
}

/** Rewrite one event in place (recursing into tuplets); null fn = remove it. */
function withEventRewritten(
  score: Score,
  found: FoundModelEvent,
  fn: ((event: NoteEvent) => NoteEvent) | null,
): Score {
  const rewrite = (list: VoiceItem[]): VoiceItem[] =>
    list.flatMap((item): VoiceItem[] => {
      if (item.kind === 'tuplet') return [{ ...item, items: rewrite(item.items) }];
      if (item.id !== found.event.id) return [item];
      return fn ? [fn(item)] : [];
    });
  const part = score.parts[found.partIndex];
  const measures = part.measures.map((pm, i) =>
    i !== found.measureIndex
      ? pm
      : {
          ...pm,
          voices: pm.voices.map((voice, v) =>
            v !== found.partVoiceIndex ? voice : { ...voice, items: rewrite(voice.items) },
          ),
        },
  );
  return {
    ...score,
    parts: score.parts.map((candidate, partIndex) =>
      partIndex === found.partIndex ? { ...part, measures } : candidate,
    ),
  };
}

/** Immediate timed neighbour in the same staff/voice, including a barline.
 * Never bridge a rest, an empty bar, or an underfilled voice. */
function adjacentModelEvent(
  score: Score,
  found: FoundModelEvent,
  direction: -1 | 1,
): FoundModelEvent | null {
  const part = score.parts[found.partIndex];
  const timedAt = (measureIndex: number) => {
    const voice = staffVoiceEntries(part, measureIndex, found.staffIndex)[found.voiceIndex]?.voice;
    return voice ? voiceTiming(voice).filter(({ duration }) => compareF(duration, ZERO) > 0) : [];
  };
  const timed = timedAt(found.measureIndex);
  const index = timed.findIndex(({ event }) => event.id === found.event.id);
  if (index < 0) return null;
  const current = timed[index];
  let neighbour = timed[index + direction];
  if (!neighbour) {
    const measureIndex = found.measureIndex + direction;
    if (!score.form[measureIndex]) return null;
    if (direction < 0) {
      if (compareF(current.start, ZERO) !== 0) return null;
      const previous = timedAt(measureIndex);
      neighbour = previous[previous.length - 1];
      if (
        !neighbour ||
        compareF(
          addF(neighbour.start, neighbour.duration),
          measureCapacity(score, measureIndex),
        ) !== 0
      )
        return null;
    } else {
      if (
        compareF(
          addF(current.start, current.duration),
          measureCapacity(score, found.measureIndex),
        ) !== 0
      )
        return null;
      neighbour = timedAt(measureIndex)[0];
      if (!neighbour || compareF(neighbour.start, ZERO) !== 0) return null;
    }
  }
  return findModelEvent(score, neighbour.event.id, {
    partId: found.partId,
    staffIndex: found.staffIndex,
  });
}

function withIncomingTie(head: NoteHead, incoming: boolean): NoteHead {
  const outgoing = head.tie === 'start' || head.tie === 'both';
  const tie = incoming ? (outgoing ? 'both' : 'stop') : outgoing ? 'start' : undefined;
  const next = { ...head };
  if (tie) next.tie = tie;
  else delete next.tie;
  return next;
}

/** Finish a pending tie when note entry reaches its matching destination.
 * Both ends are needed by native playback and MusicXML export. */
function completeIncomingTie(score: Score, id: string, target?: ContentTarget): Score {
  const found = findModelEvent(score, id, target);
  if (!found || found.event.kind !== 'note') return score;
  const previous = adjacentModelEvent(score, found, -1);
  if (!previous || previous.event.kind !== 'note') return score;
  const tiedPitches = previous.event.notes
    .filter((head) => head.tie === 'start' || head.tie === 'both')
    .map((head) => midiOf(head.pitch));
  if (!found.event.notes.some((head) => tiedPitches.includes(midiOf(head.pitch)))) return score;
  return withEventRewritten(score, found, (event) => ({
    ...event,
    notes: event.notes.map((head) =>
      tiedPitches.includes(midiOf(head.pitch)) ? withIncomingTie(head, true) : head,
    ),
  }));
}

/** Remove notation whose endpoint was deleted or replaced. */
function withoutEventSpanners(score: Score, id: string): Score {
  const current = score.spanners ?? [];
  const spanners = current.filter(
    (spanner) => spanner.fromEventId !== id && spanner.toEventId !== id,
  );
  return spanners.length === current.length ? score : { ...score, spanners };
}

/** Prune endpoints removed indirectly by replace-carving or a voice rebuild. */
function withoutDanglingSpanners(score: Score): Score {
  const current = score.spanners ?? [];
  if (current.length === 0) return score;
  const eventIds = new Set<string>();
  for (const part of score.parts) {
    for (const measure of part.measures) {
      for (const voice of measure.voices) {
        for (const event of flattenEvents(voice.items)) eventIds.add(event.id);
      }
    }
  }
  const spanners = current.filter(
    (spanner) => eventIds.has(spanner.fromEventId) && eventIds.has(spanner.toEventId),
  );
  return spanners.length === current.length ? score : { ...score, spanners };
}

function patchEventInPlace(
  score: Score,
  found: FoundModelEvent,
  patch: UpdateNoteArgs,
): Score | null {
  if (
    found.event.kind !== 'note' ||
    found.event.notes.length === 0 ||
    (patch.midi === undefined && patch.spelledAs === undefined && patch.tie === undefined)
  ) {
    return null;
  }
  if (patch.headIndex !== undefined && !found.event.notes[patch.headIndex]) return null;
  const detached =
    patch.midi !== undefined &&
    patch.midi !==
      midiOf(found.event.notes[patch.headIndex ?? editableHeadIndex(found.event.notes)].pitch)
      ? detachHeadTies(score, found, patch.headIndex ?? editableHeadIndex(found.event.notes))
      : score;
  return withEventRewritten(detached, found, (event) => {
    const headIndex = patch.headIndex ?? editableHeadIndex(event.notes);
    return {
      ...event,
      notes: event.notes.map((nh, i) => {
        if (i !== headIndex) return nh;
        const midi = patch.midi ?? midiOf(nh.pitch);
        let pitch: NoteHead['pitch'];
        try {
          pitch =
            patch.spelledAs !== undefined
              ? spellMidi(midi, { name: patch.spelledAs })
              : spellMidi(midi, { key: keyContextOf(score, found.measureIndex) });
        } catch {
          pitch = spellMidi(midi, { key: keyContextOf(score, found.measureIndex) });
        }
        const next: NoteHead = { ...nh, pitch };
        const tie = patch.tie ?? nh.tie;
        if (tie) next.tie = tie;
        return next;
      }),
    };
  });
}

function asRestEvent(event: NoteEvent): NoteEvent {
  const positioned = { ...event };
  delete positioned.articulations;
  delete positioned.ornaments;
  delete positioned.lyrics;
  delete positioned.slash;
  return { ...positioned, kind: 'rest', notes: [] };
}

// ---------------------------------------------------------------------------
// The ops
// ---------------------------------------------------------------------------

/** Read authored marks without losing grace notes or nested tuplet members. */
export function getExpressionMarks(
  score: Score,
  id: string,
  target?: ContentTarget,
): Pick<NoteEvent, 'articulations' | 'dynamic'> | null {
  const event = findModelEvent(score, id, target)?.event;
  return event ? { articulations: event.articulations, dynamic: event.dynamic } : null;
}

/** Expression edits leave the event and every enclosing rhythmic group intact. */
export function toggleArticulation(
  score: Score,
  id: string,
  articulation: Articulation,
  target?: ContentTarget,
): Score | null {
  const found = findModelEvent(score, id, target);
  if (!found || found.event.kind !== 'note') return null;
  return withEventRewritten(score, found, (event) => {
    const current = event.articulations ?? [];
    const articulations = current.includes(articulation)
      ? current.filter((mark) => mark !== articulation)
      : [...current, articulation];
    const next = { ...event };
    if (articulations.length) next.articulations = articulations;
    else delete next.articulations;
    return next;
  });
}

/** Dynamics may begin on rests; clearing a mark restores the inherited level. */
export function setDynamic(
  score: Score,
  id: string,
  dynamic: DynamicMark | undefined,
  target?: ContentTarget,
): Score | null {
  const found = findModelEvent(score, id, target);
  if (!found || found.event.dynamic === dynamic) return null;
  return withEventRewritten(score, found, (event) => {
    const next = { ...event };
    if (dynamic) next.dynamic = dynamic;
    else delete next.dynamic;
    return next;
  });
}

export interface AddNoteArgs {
  midi: number;
  /** Exact chord voicing for clipboard entry; ties are generated for new splits. */
  pitches?: NoteHead['pitch'][];
  /** Exact noteheads for semantic duplication, including partial chord ties. */
  noteheads?: NoteHead[];
  /** Fresh identity supplied by a caller that must reconnect copied spanners. */
  idBase?: string;
  /** Authored notation carried by the event (expressions, text, lyrics, slash). */
  source?: NoteEventMetadata;
  /** Shared fresh identity for pasted members of an explicit 3:2 group. */
  tupletGroup?: string;
  /** Explicit written spelling chosen by A-G entry (B# must not become C). */
  spelledAs?: string;
  /** 1-based beat within the measure, in the meter's denominator unit. */
  beat: number;
  /** Duration in beats. */
  duration: number;
  rest?: boolean;
}

/** Add a note (or rest) with replace-entry semantics, clamped to the bar.
 *  `voiceIndex` selects the voice (0 = melody, 1 = the stems-down voice). */
export function addNote(
  score: Score,
  measureIndex: number,
  note: AddNoteArgs,
  voiceIndex = 0,
  target?: ContentTarget,
): Score | null {
  return addNotes(score, measureIndex, [note], voiceIndex, target);
}

/** Write a complete paste to one voice before rebuilding its notation. A
 * triplet member alone leaves a temporarily unnotatable gap; sequential
 * single-entry writes must not reconstruct that intermediate state. */
export function addNotes(
  score: Score,
  measureIndex: number,
  notes: readonly AddNoteArgs[],
  voiceIndex = 0,
  target?: ContentTarget,
): Score | null {
  if (!score.form[measureIndex]) return null;
  if (target && !resolveTarget(score, target)?.part.measures[measureIndex]) return null;
  const bq = beatQuarters(score, measureIndex);
  const capacity = measureCapacity(score, measureIndex);
  const targetVoice = voiceAtTarget(score, measureIndex, voiceIndex, target);
  if (requiresExactTupletPreservation(targetVoice)) return null;
  const slots = voiceToSlots(targetVoice);
  let carved = slots;
  const freshIds: string[] = [];
  for (const note of notes) {
    const start = mulF(fractionFromNumber(note.beat - 1), bq);
    if (compareF(start, capacity) >= 0 || compareF(start, ZERO) < 0) return null;
    let dur = mulF(fractionFromNumber(note.duration), bq);
    if (compareF(addF(start, dur), capacity) > 0) dur = addF(capacity, negate(start));
    if (compareF(dur, ZERO) <= 0) return null;

    if (slots.some((slot) => slot.tupletGroup && slotOverlaps(slot, start, addF(start, dur)))) {
      return null;
    }
    carved = carveSlots(carved, start, addF(start, dur));
    let entryPitch: NoteHead['pitch'] | undefined;
    if (!note.rest && note.noteheads === undefined) {
      try {
        entryPitch = note.spelledAs
          ? spellMidi(note.midi, { name: note.spelledAs })
          : spellMidi(note.midi, { key: keyContextOf(score, measureIndex) });
      } catch {
        entryPitch = spellMidi(note.midi, { key: keyContextOf(score, measureIndex) });
      }
    }
    const fresh: TimedSlot = {
      start,
      quarters: dur,
      idBase: note.idBase ?? uid('mel'),
      ...(note.tupletGroup ? { tupletGroup: note.tupletGroup } : {}),
      ...(note.source ? { metadata: note.source } : {}),
      ...(note.noteheads !== undefined
        ? { noteheads: note.noteheads.map((head) => ({ ...head, pitch: { ...head.pitch } })) }
        : entryPitch
          ? {
              noteheads: (note.pitches?.length ? note.pitches : [entryPitch]).map((pitch) => ({
                pitch: { ...pitch },
              })),
            }
          : {}),
    };
    carved.push(fresh);
    freshIds.push(fresh.idBase);
  }
  if (!freshIds.length) return null;
  let items: VoiceItem[];
  try {
    items = slotsToVoiceItems(
      carved,
      capacity,
      restScopeFor(score, measureIndex, voiceIndex, target),
    );
  } catch (error) {
    // A fragment that cannot be represented by the slot writer leaves the
    // score intact. Other programming errors must still surface.
    if (error instanceof UnnotatableDurationError) return null;
    throw error;
  }
  let entered = withoutDanglingSpanners(
    withVoiceItems(score, measureIndex, items, voiceIndex, target),
  );
  for (const id of freshIds) entered = completeIncomingTie(entered, id, target);
  return entered;
}

export interface InsertTripletArgs {
  /** 1-based beat within the measure, in the meter's denominator unit. */
  beat: number;
  /** Written value of each member in beats (three occupy two of these values). */
  duration: number;
}

/** Create an explicit 3:2 group at the caret, initially filled with authored
 * rests. Normal note entry can then replace each member in place without ever
 * flattening the tuplet. The complete group must fit before the barline. */
export function insertTriplet(
  score: Score,
  measureIndex: number,
  triplet: InsertTripletArgs,
  voiceIndex = 0,
  target?: ContentTarget,
): Score | null {
  if (!score.form[measureIndex]) return null;
  if (target && !resolveTarget(score, target)?.part.measures[measureIndex]) return null;
  const bq = beatQuarters(score, measureIndex);
  const capacity = measureCapacity(score, measureIndex);
  const start = mulF(fractionFromNumber(triplet.beat - 1), bq);
  const writtenMember = mulF(fractionFromNumber(triplet.duration), bq);
  const soundingMember = mulF(writtenMember, frac(2, 3));
  const end = addF(start, mulF(writtenMember, frac(2)));
  if (
    compareF(start, ZERO) < 0 ||
    compareF(start, capacity) >= 0 ||
    compareF(writtenMember, ZERO) <= 0 ||
    compareF(end, capacity) > 0
  ) {
    return null;
  }

  const targetVoice = voiceAtTarget(score, measureIndex, voiceIndex, target);
  if (requiresExactTupletPreservation(targetVoice)) return null;
  const slots = voiceToSlots(targetVoice);
  if (slots.some((slot) => slot.tupletGroup && slotOverlaps(slot, start, end))) return null;

  const groupId = uid('tup');
  const carved = carveSlots(slots, start, end);
  for (let member = 0; member < 3; member++) {
    carved.push({
      start: addF(start, mulF(soundingMember, frac(member))),
      quarters: soundingMember,
      idBase: uid('mel'),
      tupletGroup: groupId,
    });
  }
  return withoutDanglingSpanners(
    withVoiceItems(
      score,
      measureIndex,
      slotsToVoiceItems(carved, capacity, restScopeFor(score, measureIndex, voiceIndex, target)),
      voiceIndex,
      target,
    ),
  );
}

/** Replace an authored event's content in place. This retains its exact
 * written duration and any surrounding tuplet subtree; in particular, a
 * selected ordinary event in a voice that also contains a 5:4/7:4 group must
 * not fall back to rebuilding the whole voice through slots. */
export function replaceTupletEventContent(
  score: Score,
  id: string,
  content: { midi: number; spelledAs?: string; rest?: false } | { rest: true },
  target?: ContentTarget,
): Score | null {
  const found = findModelEvent(score, id, target);
  if (!found || found.event.grace) return null;
  const voice =
    score.parts[found.partIndex]?.measures[found.measureIndex]?.voices[found.partVoiceIndex];
  if (!found.insideTuplet && !requiresExactTupletPreservation(voice)) return null;
  const replaced = withEventRewritten(score, found, (event) => {
    if (content.rest) return asRestEvent(event);
    let pitch: NoteHead['pitch'];
    try {
      pitch = content.spelledAs
        ? spellMidi(content.midi, { name: content.spelledAs })
        : spellMidi(content.midi, { key: keyContextOf(score, found.measureIndex) });
    } catch {
      pitch = spellMidi(content.midi, { key: keyContextOf(score, found.measureIndex) });
    }
    const { slash: _slash, ...pitched } = event;
    return { ...pitched, kind: 'note', notes: [{ pitch }] };
  });
  return content.rest
    ? withoutEventSpanners(replaced, id)
    : completeIncomingTie(replaced, id, target);
}

export interface UpdateNoteArgs {
  headIndex?: number;
  midi?: number;
  beat?: number;
  duration?: number;
  spelledAs?: string;
  tie?: 'start' | 'stop' | 'both';
}

/** The editor renders a chord event through its highest notehead. */
function editableHeadIndex(noteheads: NoteHead[]): number {
  return noteheads.reduce(
    (best, head, index) => (midiOf(head.pitch) > midiOf(noteheads[best].pitch) ? index : best),
    0,
  );
}

/** Slashes are addressable timed events with no sounding pitch. Their empty
 * notehead array must remain distinct from a rest's absent noteheads. */
function editableMidi(noteheads: NoteHead[] | undefined): number | null {
  return noteheads?.length ? midiOf(noteheads[editableHeadIndex(noteheads)].pitch) : null;
}

/** Patch an event. Retiming (beat/duration) carves like entry; pitch-only
 *  patches leave neighbours untouched. */
export function updateNote(
  score: Score,
  id: string,
  patch: UpdateNoteArgs,
  target?: ContentTarget,
): Score | null {
  const found = findEvent(score, id, target);
  if (!found) {
    // Grace events have no timed slot; pitch patches apply in place and
    // timing fields are meaningless (a grace occupies no measure time).
    const grace = findGraceEvent(score, id, target);
    return grace ? patchEventInPlace(score, grace, patch) : null;
  }
  const modelEvent = findModelEvent(score, id, target);
  if (!modelEvent) return null;
  if (patch.headIndex !== undefined && !modelEvent.event.notes[patch.headIndex]) return null;
  const { measureIndex, slot } = found;
  const retimed = patch.beat !== undefined || patch.duration !== undefined;

  // Content-only edits never need to flatten/reconstruct the voice. Besides
  // preserving exact tuplets, this retains articulations, lyrics and other
  // authored event metadata on ordinary notes too.
  if (!retimed) return patchEventInPlace(score, modelEvent, patch);

  const voice = score.parts[found.partIndex]?.measures[measureIndex]?.voices[found.partVoiceIndex];
  if (requiresExactTupletPreservation(voice)) return null;
  if (slot.tupletGroup) return null;

  let noteheads = slot.noteheads;
  if (
    slot.noteheads?.length &&
    (patch.midi !== undefined || patch.spelledAs !== undefined || patch.tie !== undefined)
  ) {
    const headIndex = patch.headIndex ?? editableHeadIndex(slot.noteheads!);
    noteheads = slot.noteheads!.map((nh, i) => {
      if (i !== headIndex) return nh;
      const midi = patch.midi ?? midiOf(nh.pitch);
      let pitch = nh.pitch;
      if (patch.spelledAs !== undefined) {
        try {
          pitch = spellMidi(midi, { name: patch.spelledAs });
        } catch {
          pitch = spellMidi(midi, { key: keyContextOf(score, measureIndex) });
        }
      } else if (patch.midi !== undefined) {
        pitch = spellMidi(midi, { key: keyContextOf(score, measureIndex) });
      }
      const next: NoteHead = { pitch };
      const tie = patch.tie ?? nh.tie;
      if (tie) next.tie = tie;
      return next;
    });
  }

  const bq = beatQuarters(score, measureIndex);
  const capacity = measureCapacity(score, measureIndex);
  const start =
    retimed && patch.beat !== undefined
      ? mulF(fractionFromNumber(patch.beat - 1), bq)
      : found.slot.start;
  const dur =
    retimed && patch.duration !== undefined
      ? mulF(fractionFromNumber(patch.duration), bq)
      : found.slot.quarters;
  if (compareF(start, capacity) >= 0) return null;

  const rest = found.slots.filter((_, i) => i !== found.slotIndex);
  const edited: TimedSlot = {
    start,
    quarters: dur,
    idBase: slot.idBase,
    ...(slot.source ? { source: slot.source } : {}),
    ...(noteheads ? { noteheads } : {}),
  };

  // A DURATION-ONLY change is non-destructive (user ruling Aug 2026, the
  // Guitar Pro model): the voice is a sequence, so growing a note SHIFTS what
  // follows instead of consuming it, and the bar is allowed to end up
  // overfull — the score flags it (validateScore already calls overflow a
  // warning, and the engraver draws an overfull bar) and the player fixes it
  // by adjusting durations until the bar adds up again. Shrinking pulls what
  // follows left by the same walk, which is also how an overfull bar is
  // repaired. Moving a note (a beat change) keeps replace-entry semantics —
  // dropping a note ON another is entry, and entry consumes what it lands on.
  if (patch.duration !== undefined && patch.beat === undefined) {
    // Displacing a tuplet group would need the group re-anchored at a new
    // offset; refuse rather than guess (same conservatism as the carve path).
    const end = addF(start, dur);
    if (rest.some((s) => s.tupletGroup && compareF(addF(s.start, s.quarters), start) > 0)) {
      if (rest.some((s) => s.tupletGroup && slotOverlaps(s, start, end))) return null;
    }
    // List semantics from the edit onward: everything BEFORE the edited note
    // is untouched, and everything after it moves by exactly the change in
    // length. Generated rests (`r:`/`r-` ids) in the tail are dropped and
    // regenerated against capacity, which keeps the GAPS between the notes
    // at their written size (entry, a carve's leftover and a lifted chart
    // all write their rests with these ids, so an interior one is the rhythm
    // the user wrote, not free space) while trailing padding absorbs a
    // growth. Packing the tail end to end instead deleted those gaps, so
    // even a growth could pull a later note earlier.
    const delta = addF(dur, negate(slot.quarters));
    const before = rest.filter((s) => compareF(s.start, start) < 0);
    const tailSlots = rest
      .filter((s) => compareF(s.start, start) >= 0)
      .filter((s) => s.noteheads || !/^r[:-]/.test(s.idBase));
    const shifted: TimedSlot[] = [...before, edited];
    for (const s of tailSlots) {
      shifted.push(delta.num === 0 ? s : { ...s, start: addF(s.start, delta) });
    }
    return withoutDanglingSpanners(
      withVoiceItems(
        score,
        measureIndex,
        slotsToVoiceItems(
          shifted,
          capacity,
          `${score.form[measureIndex].id}:${found.partId}:s${found.staffIndex}:v${found.voiceIndex}`,
        ),
        found.voiceIndex,
        { partId: found.partId, staffIndex: found.staffIndex },
      ),
    );
  }

  // Beat moves keep the historical clamp: a note dropped near the barline
  // shortens to fit rather than spilling over.
  const clamped = compareF(addF(start, dur), capacity) > 0 ? addF(capacity, negate(start)) : dur;
  let slots = rest;
  if (slots.some((s) => s.tupletGroup && slotOverlaps(s, start, addF(start, clamped)))) {
    return null;
  }
  if (retimed) slots = carveSlots(slots, start, addF(start, clamped));
  slots.push({ ...edited, quarters: clamped });
  return withoutDanglingSpanners(
    withVoiceItems(
      score,
      measureIndex,
      slotsToVoiceItems(
        slots,
        capacity,
        `${score.form[measureIndex].id}:${found.partId}:s${found.staffIndex}:v${found.voiceIndex}`,
      ),
      found.voiceIndex,
      { partId: found.partId, staffIndex: found.staffIndex },
    ),
  );
}

/** Delete an event (note OR rest — rests are real content in the model). */
export function deleteNote(score: Score, id: string, target?: ContentTarget): Score | null {
  const found = findEvent(score, id, target);
  if (!found) {
    const grace = findGraceEvent(score, id, target);
    if (!grace) return null;
    const next = withEventRewritten(score, grace, null);
    return withoutEventSpanners(next, id);
  }
  const modelEvent = findModelEvent(score, id, target);
  const voice =
    score.parts[found.partIndex]?.measures[found.measureIndex]?.voices[found.partVoiceIndex];
  if (modelEvent && (modelEvent.insideTuplet || requiresExactTupletPreservation(voice))) {
    // Removing a member changes a tuplet's written span. Preserve the exact
    // group (and mixed voices containing one) by replacing the event with an
    // equal written-duration rest instead.
    if (modelEvent.event.kind === 'rest') return null;
    return withoutEventSpanners(withEventRewritten(score, modelEvent, asRestEvent), id);
  }
  const remaining = found.slots.filter((_, i) => i !== found.slotIndex);
  const capacity = measureCapacity(score, found.measureIndex);
  // Synthetic padding alone collapses to "no voice", but an authored rest is
  // real score content. Deleting one authored rest must not erase its siblings.
  const authored = remaining.filter((slot) => slot.noteheads || !/^r[:-]/.test(slot.idBase));
  const items =
    authored.length === 0
      ? []
      : slotsToVoiceItems(
          remaining,
          capacity,
          `${score.form[found.measureIndex].id}:${found.partId}:s${found.staffIndex}:v${found.voiceIndex}`,
        );
  return withoutEventSpanners(
    withVoiceItems(score, found.measureIndex, items, found.voiceIndex, {
      partId: found.partId,
      staffIndex: found.staffIndex,
    }),
    id,
  );
}

/** Read authored heads in stable model order, including grace and nested tuplets. */
export function getNoteHeads(
  score: Score,
  id: string,
  target?: ContentTarget,
): readonly NoteHead[] | null {
  return findModelEvent(score, id, target)?.event.notes ?? null;
}

export function defaultNoteHeadIndex(score: Score, id: string, target?: ContentTarget): number {
  const heads = getNoteHeads(score, id, target);
  return heads?.length ? editableHeadIndex([...heads]) : 0;
}

/** Changing/removing one head breaks its ties at both ends, leaving other
 * chord tones and their ties intact. Unison duplicates can still carry a tie. */
function detachHeadTies(score: Score, found: FoundModelEvent, headIndex: number): Score {
  const head = found.event.notes[headIndex];
  if (!head?.tie) return score;
  const midi = midiOf(head.pitch);
  let next = withEventRewritten(score, found, (event) => ({
    ...event,
    notes: event.notes.map((h, i) => {
      if (i !== headIndex) return h;
      const untied = { ...h };
      delete untied.tie;
      return untied;
    }),
  }));
  for (const direction of [-1, 1] as const) {
    const side = direction === 1 ? ['start', 'both'] : ['stop', 'both'];
    if (!side.includes(head.tie)) continue;
    if (
      found.event.notes.some(
        (h, i) => i !== headIndex && midiOf(h.pitch) === midi && h.tie && side.includes(h.tie),
      )
    )
      continue;
    const neighbour = adjacentModelEvent(next, found, direction);
    if (!neighbour) continue;
    next = withEventRewritten(next, neighbour, (event) => ({
      ...event,
      notes: event.notes.map((h) => {
        if (midiOf(h.pitch) !== midi) return h;
        if (direction === 1) return withIncomingTie(h, false);
        const incoming = h.tie === 'stop' || h.tie === 'both';
        const untied = { ...h };
        if (incoming) untied.tie = 'stop';
        else delete untied.tie;
        return untied;
      }),
    }));
  }
  return next;
}

/** Add a head without retiming or rebuilding the event/tuplet. */
export function addChordHead(
  score: Score,
  id: string,
  pitch: NoteHead['pitch'],
  target?: ContentTarget,
): Score | null {
  const found = findModelEvent(score, id, target);
  const midi = midiOf(pitch);
  if (
    !found ||
    found.event.kind !== 'note' ||
    !found.event.notes.length ||
    !Number.isInteger(midi) ||
    midi < 0 ||
    midi > 127 ||
    !Number.isInteger(pitch.alter) ||
    Math.abs(pitch.alter) > 2 ||
    found.event.notes.some(
      (h) => noteNameOf(h.pitch) === noteNameOf(pitch) && h.pitch.octave === pitch.octave,
    )
  )
    return null;
  return withEventRewritten(score, found, (event) => ({
    ...event,
    notes: [...event.notes, { pitch: { ...pitch } }],
  }));
}

/** Remove just the chosen head; the final head becomes an authored rest. */
export function removeChordHead(
  score: Score,
  id: string,
  headIndex: number,
  target?: ContentTarget,
): Score | null {
  const found = findModelEvent(score, id, target);
  if (!found?.event.notes[headIndex]) return null;
  const detached = detachHeadTies(score, found, headIndex);
  const next = withEventRewritten(detached, found, (event) =>
    event.notes.length === 1
      ? asRestEvent(event)
      : { ...event, notes: event.notes.filter((_, index) => index !== headIndex) },
  );
  return found.event.notes.length === 1 ? withoutEventSpanners(next, id) : next;
}

/** Move a sounding pitch by semitones. Rests and unpitched slashes are no-ops. */
export function nudgeNote(
  score: Score,
  id: string,
  semitones: number,
  target?: ContentTarget,
  headIndex?: number,
): Score | null {
  const head = getNoteHeads(score, id, target)?.[
    headIndex ?? defaultNoteHeadIndex(score, id, target)
  ];
  if (!head) return null;
  const midi = midiOf(head.pitch) + semitones;
  if (!Number.isInteger(midi) || midi < 0 || midi > 127 || semitones === 0) return null;
  return updateNote(score, id, { midi, headIndex }, target);
}

/** Naturals with a single-accidental enharmonic counterpart. This lets the
 * MuseScore-style respell command reach harmonic spellings such as F♭ without
 * opening the door to double accidentals for D, G, or A. */
const NATURAL_ENHARMONIC: Readonly<Record<string, string>> = {
  C: 'B#',
  E: 'Fb',
  F: 'E#',
  B: 'Cb',
};

/** Flip an event's top notehead to another single-accidental spelling
 * (C♯ ↔ D♭, E ↔ F♭, F ↔ E♯, B ↔ C♭). */
export function respellNote(
  score: Score,
  id: string,
  target?: ContentTarget,
  headIndex?: number,
): Score | null {
  const found = findEvent(score, id, target);
  const graceNotes = !found ? findGraceEvent(score, id, target)?.event.notes : undefined;
  const heads = found?.slot.noteheads ?? graceNotes;
  if (!heads?.length) return null;
  const head = heads[headIndex ?? editableHeadIndex(heads)];
  if (!head) return null;
  const pitch = head.pitch;
  const name = noteNameOf(pitch);
  const pc = pitchClassOf(name);
  const flipped = name.includes('#')
    ? CHROMATIC_FLAT[pc]
    : name.includes('b')
      ? CHROMATIC_SHARP[pc]
      : (NATURAL_ENHARMONIC[name] ?? name);
  if (flipped === name) return null;
  // Guard: only flip when the respelling exists at single-accidental depth.
  parseSpelledPitchName(flipped);
  return updateNote(score, id, { spelledAs: flipped, midi: midiOf(pitch), headIndex }, target);
}

/** Toggle the editable notehead's outgoing tie and the matching destination's
 * incoming side. A missing destination leaves a pending tie for note entry.
 * Rewrite in place so arbitrary and nested tuplets remain structurally exact. */
export function toggleOutgoingTie(
  score: Score,
  id: string,
  target?: ContentTarget,
  selectedHeadIndex?: number,
): Score | null {
  const found = findModelEvent(score, id, target);
  if (!found || found.event.kind !== 'note' || found.event.notes.length === 0) return null;
  const headIndex = selectedHeadIndex ?? editableHeadIndex(found.event.notes);
  if (!found.event.notes[headIndex]) return null;
  const sourceHead = found.event.notes[headIndex];
  const outgoing = sourceHead.tie === 'start' || sourceHead.tie === 'both';
  const toggled = withEventRewritten(score, found, (event) => {
    return {
      ...event,
      notes: event.notes.map((head, index) => {
        if (index !== headIndex) return head;
        const tie = outgoing
          ? head.tie === 'both'
            ? ('stop' as const)
            : undefined
          : head.tie === 'stop'
            ? ('both' as const)
            : ('start' as const);
        const next: NoteHead = { ...head };
        if (tie) next.tie = tie;
        else delete next.tie;
        return next;
      }),
    };
  });
  const destination = adjacentModelEvent(toggled, found, 1);
  if (!destination || destination.event.kind !== 'note') return toggled;
  const midi = midiOf(sourceHead.pitch);
  if (!destination.event.notes.some((head) => midiOf(head.pitch) === midi)) return toggled;
  return withEventRewritten(toggled, destination, (event) => ({
    ...event,
    notes: event.notes.map((head) =>
      midiOf(head.pitch) === midi
        ? withIncomingTie(
            head,
            !outgoing ||
              found.event.notes.some(
                (other, index) =>
                  index !== headIndex &&
                  midiOf(other.pitch) === midi &&
                  (other.tie === 'start' || other.tie === 'both'),
              ),
          )
        : head,
    ),
  }));
}

/**
 * Append a pitch after the last melody note (piano entry): fills the last
 * measure, or grows the form by one bar when the note doesn't fit (the final
 * barline follows via normalizeScore). Spelling follows the key.
 */
function appendIntoFreshMeasure(
  score: Score,
  midi: number,
  duration: Fraction,
  target?: ContentTarget,
): Score {
  const freshId = uid('m');
  const form = [...score.form.map((measure) => ({ ...measure })), { id: freshId, harmony: [] }];
  const parts = score.parts.map((part) => ({
    ...part,
    measures: [...part.measures, { id: `pm:${freshId}`, voices: [] }],
  }));
  const grown: Score = { ...score, form, parts };
  const freshIndex = form.length - 1;
  const capacity = measureCapacity(grown, freshIndex);
  const fittedDuration = compareF(duration, capacity) > 0 ? capacity : duration;
  if (compareF(fittedDuration, ZERO) <= 0) return score;
  const notehead: NoteHead = {
    pitch: spellMidi(midi, { key: keyContextOf(grown, freshIndex) }),
  };
  const id = uid('mel');
  const items = slotsToVoiceItems(
    [
      {
        start: ZERO,
        quarters: fittedDuration,
        idBase: id,
        noteheads: [notehead],
      },
    ],
    capacity,
    restScopeFor(grown, freshIndex, 0, target),
  );
  return completeIncomingTie(withVoiceItems(grown, freshIndex, items, 0, target), id, target);
}

export function appendNote(
  score: Score,
  midi: number,
  durationBeats: number,
  target?: ContentTarget,
): Score {
  const lastIndex = score.form.length - 1;
  const bq = beatQuarters(score, Math.max(0, lastIndex));
  const dur = mulF(fractionFromNumber(durationBeats), bq);
  if (compareF(dur, ZERO) <= 0) return score;
  if (lastIndex < 0) return appendIntoFreshMeasure(score, midi, dur, target);

  const capacity = measureCapacity(score, lastIndex);
  const targetVoice = voiceAtTarget(score, lastIndex, 0, target);
  // Appending via slots would flatten an arbitrary/nested tuplet already in
  // the last voice. Preserve the score as a safe piano-entry no-op instead.
  if (requiresExactTupletPreservation(targetVoice)) return score;
  const slots = voiceToSlots(targetVoice);
  // Append after the last PITCHED slot (trailing synthesized rests are free
  // space, matching the chart op's "used ticks" reckoning over real notes).
  const used = slots
    .filter((s) => s.noteheads)
    .reduce((mx, s) => {
      const end = addF(s.start, s.quarters);
      return compareF(end, mx) > 0 ? end : mx;
    }, ZERO);
  const notehead: NoteHead = {
    pitch: spellMidi(midi, { key: keyContextOf(score, lastIndex) }),
  };

  if (compareF(addF(used, dur), capacity) <= 0) {
    const carved = carveSlots(slots, used, addF(used, dur));
    const id = uid('mel');
    carved.push({ start: used, quarters: dur, idBase: id, noteheads: [notehead] });
    const appended = withoutDanglingSpanners(
      withVoiceItems(
        score,
        lastIndex,
        slotsToVoiceItems(carved, capacity, restScopeFor(score, lastIndex, 0, target)),
        0,
        target,
      ),
    );
    return completeIncomingTie(appended, id, target);
  }

  return appendIntoFreshMeasure(score, midi, dur, target);
}

/** Remove authored content while leaving the form/harmony lane intact.
 * Legacy no-target calls remove every content part. An explicit target clears
 * only that staff and keeps its instrument definition available for editing. */
export function clearMelody(score: Score, target?: ContentTarget): Score {
  if (!target) return withoutDanglingSpanners({ ...score, parts: [] });
  const resolved = resolveTarget(score, target);
  if (!resolved) return score;
  const measures = resolved.part.measures.map((measure) => ({
    ...measure,
    voices: measure.voices.filter((voice) => staffOf(voice) !== resolved.staffIndex),
  }));
  return withoutDanglingSpanners({
    ...score,
    parts: score.parts.map((part, partIndex) =>
      partIndex === resolved.partIndex ? { ...part, measures } : part,
    ),
  });
}

/** The event's sounding info in the editor's beat domain (for commands). */
export function describeEvent(
  score: Score,
  id: string,
  target?: ContentTarget,
): {
  partId: string;
  partIndex: number;
  staffIndex: number;
  measureIndex: number;
  voiceIndex: number;
  midi: number | null;
  beat: number;
  duration: number;
  rest: boolean;
} | null {
  const found = findEvent(score, id, target);
  if (!found) {
    const grace = findGraceEvent(score, id, target);
    if (!grace) return null;
    const part = score.parts[grace.partIndex];
    const timed = voiceTiming(part.measures[grace.measureIndex].voices[grace.partVoiceIndex]).find(
      (t) => t.event.id === id,
    );
    const bq = beatQuarters(score, grace.measureIndex);
    return {
      partId: grace.partId,
      partIndex: grace.partIndex,
      staffIndex: grace.staffIndex,
      measureIndex: grace.measureIndex,
      voiceIndex: grace.voiceIndex,
      midi: editableMidi(grace.event.notes),
      beat: 1 + (timed ? fractionToNumber(timed.start) / fractionToNumber(bq) : 0),
      duration: 0,
      rest: false,
    };
  }
  const bq = beatQuarters(score, found.measureIndex);
  const toBeats = (q: Fraction) => fractionToNumber(q) / fractionToNumber(bq);
  return {
    partId: found.partId,
    partIndex: found.partIndex,
    staffIndex: found.staffIndex,
    measureIndex: found.measureIndex,
    voiceIndex: found.voiceIndex,
    midi: editableMidi(found.slot.noteheads),
    beat: 1 + toBeats(found.slot.start),
    duration: toBeats(found.slot.quarters),
    rest: !found.slot.noteheads,
  };
}
